import type { GameState, GameMode, NumberBlock } from './types'
import {
  START_TIME, PRACTICE_TIME, CLEAR_BONUS_TIME, STAGE_BONUS_TIME,
  STAGE_CLEAR_PER_BLOCK, boardSizeForStage, scoreForCount,
} from '@/config/balance'
import { makeRng } from './rng'
import { generateValidBlocks } from './board'
import { selectedSum, hasAnyMove } from './rules'

export type ClearEffect =
  | { type: 'cleared'; clearedIds: number[]; gainedScore: number; bonusTime: number }
  | { type: 'stageClear'; remainingIds: number[]; bonusScore: number }
  | { type: 'invalid' }

export function createInitialState(mode: GameMode, isLargeScreen: boolean, seed: number): GameState {
  const stage = 1
  const boardSize = boardSizeForStage(stage, isLargeScreen)
  return {
    blocks: generateValidBlocks(boardSize, makeRng(seed)),
    boardSize, selectedIds: [], score: 0, stage,
    timeRemaining: mode === 'practice' ? PRACTICE_TIME : START_TIME,
    status: 'playing', mode, isLargeScreen,
  }
}

export function setSelection(s: GameState, ids: number[]): GameState {
  return { ...s, selectedIds: ids }
}

export function currentSelectedSum(s: GameState): number {
  return selectedSum(s.blocks, s.selectedIds)
}

export function canClear(s: GameState): boolean {
  return s.status === 'playing' && s.selectedIds.length > 0 && currentSelectedSum(s) === 10
}

export function clearSelection(s: GameState, seed: number): { state: GameState; effect: ClearEffect } {
  if (!canClear(s)) return { state: s, effect: { type: 'invalid' } }
  const ids = new Set(s.selectedIds)
  const clearedIds = [...s.selectedIds]
  const blocks: NumberBlock[] = s.blocks.map(b => (ids.has(b.id) ? { ...b, value: 0 } : b))
  const gainedScore = scoreForCount(clearedIds.length)
  let state: GameState = {
    ...s, blocks, selectedIds: [],
    score: s.score + gainedScore,
    timeRemaining: s.timeRemaining + CLEAR_BONUS_TIME,
  }
  if (!hasAnyMove(blocks, s.boardSize)) {
    const remainingIds = blocks.filter(b => b.value !== 0).map(b => b.id)
    const bonusScore = remainingIds.length * STAGE_CLEAR_PER_BLOCK
    state = { ...state, score: state.score + bonusScore, status: 'stageCompleting' }
    return { state, effect: { type: 'stageClear', remainingIds, bonusScore } }
  }
  return { state, effect: { type: 'cleared', clearedIds, gainedScore, bonusTime: CLEAR_BONUS_TIME } }
}

export function advanceStage(s: GameState, seed: number): GameState {
  const stage = s.stage + 1
  const boardSize = boardSizeForStage(stage, s.isLargeScreen)
  return {
    ...s, stage, boardSize,
    blocks: generateValidBlocks(boardSize, makeRng(seed)),
    timeRemaining: s.timeRemaining + STAGE_BONUS_TIME,
    status: 'playing', selectedIds: [],
  }
}

export function tick(s: GameState): GameState {
  if (s.status !== 'playing') return s
  if (s.mode === 'practice') return s
  const t = s.timeRemaining - 1
  if (t <= 0) return { ...s, timeRemaining: 0, status: 'gameOver' }
  return { ...s, timeRemaining: t }
}
